/**
 * Settings — `quoflow.quoflow.quoflow.api.settings.*` (Task 11). Live shapes verified
 * 2026-08-04:
 *
 *   GET  settings.get_pricing_rules  → {enforce, rules, category_margins, default_markup_pct,
 *                                        base_currency}
 *   POST settings.save_pricing_rules   the same shape MINUS base_currency
 *
 * One endpoint now, not three: the old `/api/v1` split (`/settings/pricing-rules`,
 * `/settings/category-margins`, `/settings/company`) is gone, and `category_margins`/
 * `default_markup_pct` are bundled straight onto `PricingRulesSettings` (see that type's own
 * comment in `../types`). `useCategoryMargins`/`useCompanySettings` are RETIRED — every caller
 * now reads `usePricingRules().data.category_margins` / `.default_markup_pct` directly.
 * `CompanySettings.commission_pct`/`.convenience` moved onto `Quotation` itself
 * (`management_commission`/`convenience_amount` — see `useQuotation.ts`); nothing here replaces
 * them, because nothing on this transport still needs a COMPANY-WIDE commission or convenience
 * figure once each document carries its own.
 *
 * The quotation screen reads this for its live violation flags, the category-margin default and
 * the default markup. It changes rarely, so it is cached for the session.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { callGet, callPost } from './bosun'
import type { BosunApiError } from './errors'
import { num, str } from './wire'
import { getBaseCurrency, setBaseCurrency, __resetBaseCurrencyForTests } from './baseCurrency'
import { qk } from './keys'
import { ITEM_CATEGORIES, type CategoryMargins, type Currency, type ItemCategory, type PricingRule, type PricingRulesSettings } from '../types'

const SETTINGS_STALE_MS = 5 * 60_000

/* ============================================================================================
 * settings.get_pricing_rules → usePricingRules
 * ============================================================================================ */

export interface WirePricingRule {
  id: string
  kind: string
  pct?: number
  step?: number
  /** Not in the brief's example shape but read defensively — see `toRule` below: without a real
   * category, a `minMarkupCat` rule cannot be represented (B2 also means this branch is unlikely
   * to ever carry real data — `Item.category` itself is unset on the live bench). */
  cat?: string
}

export interface WirePricingRules {
  enforce: boolean | 0 | 1
  rules: WirePricingRule[]
  category_margins?: Record<string, number>
  default_markup_pct?: number
  /** `null` on the live bench — defect B5, see the module header. */
  base_currency?: string | null
}

function isItemCategory(value: string | undefined): value is ItemCategory {
  return !!value && (ITEM_CATEGORIES as readonly string[]).includes(value)
}

/**
 * `null`/unrecognised `kind` and a `minMarkupCat` with no usable `cat` are DROPPED, not
 * guessed — a rule this app cannot faithfully represent is worse silently mis-enforced than
 * simply absent (the `enforce` flag and the other rules still apply).
 */
function toRule(w: WirePricingRule): PricingRule | null {
  const id = str(w.id)
  switch (w.kind) {
    case 'noBelowCost':
      return { id, kind: 'noBelowCost' }
    case 'minMarkup':
      return { id, kind: 'minMarkup', pct: num(w.pct) }
    case 'minMarkupCat':
      return isItemCategory(w.cat) ? { id, kind: 'minMarkupCat', cat: w.cat, pct: num(w.pct) } : null
    case 'roundSell':
      return { id, kind: 'roundSell', step: num(w.step) }
    default:
      return null
  }
}

function toCategoryMargins(w: Record<string, number> | undefined): CategoryMargins {
  const out = {} as CategoryMargins
  for (const cat of ITEM_CATEGORIES) out[cat] = num(w?.[cat])
  return out
}

/**
 * PUBLISHES the base currency app-wide as a side effect.
 *
 * This is the one endpoint that knows it, and the mappers that need it are pure functions inside
 * `queryFn`s that cannot call hooks — hence `setBaseCurrency` (`baseCurrency.ts`) rather than
 * threading the value through a dozen signatures. Defect `D5` is closed: this field read `null`
 * for months and the app hardcoded `AED`; it returns a real value now, and `setBaseCurrency`
 * refuses an empty one rather than replacing a usable fallback with nothing.
 */
export function toPricingRules(w: WirePricingRules): PricingRulesSettings {
  setBaseCurrency(w.base_currency)
  const base_currency = getBaseCurrency() as Currency
  return {
    enforce: w.enforce === true || w.enforce === 1,
    rules: (w.rules ?? []).map(toRule).filter((r): r is PricingRule => r !== null),
    category_margins: toCategoryMargins(w.category_margins),
    default_markup_pct: num(w.default_markup_pct),
    base_currency,
  }
}

async function fetchPricingRules(): Promise<PricingRulesSettings> {
  return toPricingRules(await callGet<WirePricingRules>('settings', 'get_pricing_rules'))
}

export function usePricingRules() {
  return useQuery({
    queryKey: qk.settingsPricingRules(),
    queryFn: fetchPricingRules,
    staleTime: SETTINGS_STALE_MS,
  })
}

/* ============================================================================================
 * settings.save_pricing_rules → useSavePricingRules
 * ============================================================================================ */

function fromRule(rule: PricingRule): WirePricingRule {
  switch (rule.kind) {
    case 'noBelowCost':
      return { id: rule.id, kind: rule.kind }
    case 'minMarkup':
      return { id: rule.id, kind: rule.kind, pct: rule.pct }
    case 'minMarkupCat':
      return { id: rule.id, kind: rule.kind, cat: rule.cat, pct: rule.pct }
    case 'roundSell':
      return { id: rule.id, kind: rule.kind, step: rule.step }
  }
}

/** POSTs the same shape MINUS `base_currency` (contract: read-only, `Company.default_currency`). */
async function savePricingRules(settings: PricingRulesSettings): Promise<PricingRulesSettings> {
  const w = await callPost<WirePricingRules>('settings', 'save_pricing_rules', {
    enforce: settings.enforce,
    rules: settings.rules.map(fromRule),
    category_margins: settings.category_margins,
    default_markup_pct: settings.default_markup_pct,
  })
  return toPricingRules(w)
}

export function useSavePricingRules(options?: {
  onSuccess?: (data: PricingRulesSettings) => void
  onError?: (error: BosunApiError) => void
}) {
  const queryClient = useQueryClient()
  return useMutation<PricingRulesSettings, BosunApiError, PricingRulesSettings>({
    mutationFn: savePricingRules,
    onSuccess: (data) => {
      queryClient.setQueryData(qk.settingsPricingRules(), data)
      options?.onSuccess?.(data)
    },
    onError: (error) => options?.onError?.(error),
  })
}

/* ---------- test-only export ---------- */

export const __test__ = {
  savePricingRules,
  /** The base-currency warning moved to `baseCurrency.ts` when `D5` was closed — reset it there
   * with `__resetBaseCurrencyForTests`. */
  resetB5Warning: __resetBaseCurrencyForTests,
}

/* ============================================================================================
 * data_ops.get_email_templates → useEmailTemplate
 * ============================================================================================ */

export interface WireEmailTemplate {
  name?: string
  subject?: string
  response?: string
  use_html?: number | string | boolean
}

/**
 * One stored `Email Template`, by name — `Rfq` is the only one on the bench today.
 *
 * The RFQ send flow renders this rather than composing its own body: the template carries the
 * business's rules for how a quotation must come back (the workbook, unmodified — any other
 * format rejected), which our own note never said. `send_rfq_email_with_excel` does NOT render
 * it — sending with no `message` produced its own hardcoded default instead (measured
 * 2026-08-07) — so it only reaches a supplier if we fill it in and pass it as `message`.
 *
 * Returns `null` rather than throwing when the template is absent: a bench without it must still
 * be able to send an RFQ, falling back to `defaultRfqNote`.
 */
export function useEmailTemplate(template: string) {
  return useQuery({
    queryKey: ['email-template', template] as const,
    queryFn: async () => {
      const w = await callGet<unknown>('data_ops', 'get_email_templates', { template })
      const rows = Array.isArray(w)
        ? w
        : Array.isArray((w as { data?: unknown[] } | null)?.data)
          ? ((w as { data: unknown[] }).data)
          : w
            ? [w]
            : []
      const row = rows.find((r): r is WireEmailTemplate => !!r && typeof r === 'object')
      return row ?? null
    },
    enabled: !!template,
    // Templates are edited rarely and by a human; refetching them per screen visit is waste.
    staleTime: 10 * 60_000,
  })
}
